import type { ReactNode } from 'react'

import { cn } from '@/lib/cn'

/**
 * Page furniture: the title row, and sections as white rounded cards that
 * float on the page (27 Oct 2026, the Trello look).
 */

export function PageHeader({
  eyebrow,
  title,
  meta,
  actions,
  className,
}: {
  /** Where you are, in small caps: 'Pipeline', 'Settings'. */
  eyebrow?: string
  title: ReactNode
  /** A line of facts under the title — dates, counts, status. */
  meta?: ReactNode
  actions?: ReactNode
  className?: string
}) {
  return (
    <header className={cn('flex flex-wrap items-end justify-between gap-x-6 gap-y-3 pb-4', className)}>
      <div className="min-w-0">
        {eyebrow ? <p className="label mb-1.5">{eyebrow}</p> : null}
        <h1 className="text-[length:var(--text-2xl)] font-bold leading-none">{title}</h1>
        {meta ? <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[length:var(--text-sm)] text-taupe">{meta}</div> : null}
      </div>
      {actions ? <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div> : null}
    </header>
  )
}

export function Page({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('mx-auto w-full max-w-[84rem] px-5 py-6 lg:px-8', className)}>{children}</div>
}

export function Section({
  title,
  description,
  actions,
  children,
  className,
}: {
  title?: string
  description?: string
  actions?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <section className={cn('card mb-4 p-5', className)}>
      {title ? (
        <div className="mb-3.5 flex flex-wrap items-baseline justify-between gap-3">
          <div>
            <h2 className="text-[length:var(--text-base)] font-semibold">{title}</h2>
            {description ? <p className="measure mt-1 text-[length:var(--text-sm)] text-taupe">{description}</p> : null}
          </div>
          {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
        </div>
      ) : null}
      {children}
    </section>
  )
}

/**
 * A label/value pair. The workhorse of every detail page — the ledger applied
 * to a single record rather than a list of them.
 */
export function DataRow({
  label,
  children,
  className,
}: {
  label: string
  children: ReactNode
  className?: string
}) {
  return (
    <div className={cn('flex items-baseline justify-between gap-6 border-b py-2', className)}>
      <dt className="label shrink-0">{label}</dt>
      <dd className="min-w-0 text-right text-[length:var(--text-sm)]">{children}</dd>
    </div>
  )
}

export function DataList({ children, className }: { children: ReactNode; className?: string }) {
  return <dl className={cn('flex flex-col', className)}>{children}</dl>
}
